import type { FC, ReactNode } from 'react'

import { UserRound } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'
import { PisaChart } from '@/components/landing/pisa-chart'
import { PAIN_POINTS, STATS } from '@/constants/leesritme'

export const ProblemSection: FC = (): ReactNode => {
    return (
        <section id='probleem' className='py-20 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='grid items-end gap-8 lg:grid-cols-[1.4fr_1fr]'>
                    <SectionHeading
                        eyebrow='Het probleem'
                        title={(
                            <>
                                <span className='text-coral-deep'>1 op de 4</span> kinderen haalt eind groep 8 het streefniveau lezen niet.
                            </>
                        )}
                    />

                    <div className='flex items-center gap-4 lg:justify-end'>
                        <div className='flex gap-1.5' aria-hidden='true'>
                            {[0, 1, 2, 3].map(index => (
                                <span
                                    key={index}
                                    className={cn(
                                        'grid size-12 place-items-center rounded-2xl',
                                        index === 3 ? 'bg-coral text-white' : 'bg-leaf-soft text-leaf'
                                    )}
                                >
                                    <UserRound className='size-6' />
                                </span>
                            ))}
                        </div>

                        <p className='max-w-[12rem] text-sm leading-snug text-muted-foreground'>
                            Ruim een kwart haalt niveau 2F niet aan het eind van de basisschool.
                        </p>
                    </div>
                </div>

                <div className='mt-12 grid gap-6 lg:grid-cols-[1.35fr_1fr]'>
                    <div className='reveal'>
                        <PisaChart />
                    </div>

                    <ul className='grid gap-4'>
                        {STATS.map(stat => (
                            <li key={stat.value} className='reveal flex flex-col justify-between rounded-[1.5rem] border border-ink/10 bg-card p-6 shadow-soft'>
                                <p className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                                    <span className='font-heading font-soft text-5xl leading-none font-semibold tracking-tight text-ink'>
                                        {stat.value}
                                    </span>

                                    <span className='text-[1.0625rem] leading-snug text-ink/85'>
                                        {stat.label}
                                    </span>
                                </p>

                                <a
                                    href={stat.source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='mt-4 text-xs text-muted-foreground underline decoration-ink/20 underline-offset-2 hover:text-ink hover:decoration-ink'
                                >
                                    {stat.source.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='mt-20'>
                    <h3 className='font-heading text-2xl font-semibold text-ink sm:text-3xl'>
                        Waar scholen tegenaan lopen
                    </h3>

                    <ul className='mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                        {PAIN_POINTS.map((point, index) => {
                            const Icon = point.icon

                            return (
                                <li key={point.title} className='reveal relative rounded-[1.5rem] bg-sand/70 p-6'>
                                    <span className='absolute top-6 right-6 font-heading text-sm font-semibold text-ink/30 tabular-nums'>
                                        0{index + 1}
                                    </span>

                                    <span className='grid size-11 place-items-center rounded-xl bg-white text-coral-deep shadow-soft'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <h4 className='mt-5 font-heading text-lg leading-snug font-semibold text-ink'>
                                        {point.title}
                                    </h4>

                                    <p className='mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground'>
                                        {point.text}
                                    </p>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </div>
        </section>
    )
}
